import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CForm, CFormLabel, CFormInput, CFormTextarea,
  CButton, CRow, CCol, CAlert, CSpinner, CFormCheck,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilUser, cilTask, cilPhone, cilNotes, cilLaptop,
  cilSend, cilSave, cilArrowLeft, cilSearch, cilShieldAlt, cilLocationPin, cilCamera, cilInfo,
} from '@coreui/icons';
import api from '../services/api';
import SmsModal from '../components/SmsModal';
import WhatsAppModal from '../components/WhatsAppModal';
import SerialQrScanModal from '../components/SerialQrScanModal';

const STATUS_OPTIONS = ['Baru', 'Dalam Proses', 'Selesai', 'Dipulangkan', 'Menunggu Maklumbalas', 'Waiting to Pickup'];
const PRIORITY_OPTIONS = ['Biasa', 'Sederhana', 'Penting'];
const CONTACT_OPTIONS = ['SMS', 'EMAIL', 'TEL/PEJ', 'WHATSAPP'];
const EQUIPMENT_OPTIONS = ['Laptop', 'Desktop', 'Printer', 'Laptop Power Adapter', 'System Disc', 'Monitor', 'Keyboard', 'Mouse', 'Others'];

const EMPTY = {
  custid: '', jobcode: '', perhubungan: [], keutamaan: 'Biasa', catatan: '',
  jenis: '', kos: '', alat: [], peralatan_lain: '', status: 'Baru',
  no_siri: '', tempat_claim: '',
};

const CUSTOMER_INFO_FIELDS = [
  { key: 'nama', label: 'Name' },
  { key: 'uid', label: 'Customer UID' },
  { key: 'user_id', label: 'User ID' },
  { key: 'kp', label: 'IC / ID (KP)' },
  { key: 'sykt', label: 'Company (Sykt)' },
  { key: 'telhp', label: 'Mobile' },
  { key: 'telpej', label: 'Office phone' },
  { key: 'email', label: 'Email' },
  { key: 'alamat1', label: 'Address line 1' },
  { key: 'alamat2', label: 'Address line 2' },
  { key: 'poskod', label: 'Postcode' },
  { key: 'daerah', label: 'District' },
  { key: 'negeri', label: 'State' },
  { key: 'catatan', label: 'Notes' },
  { key: 'regdate', label: 'Registered date' },
  { key: 'regby', label: 'Registered by' },
  { key: 'date', label: 'Record date' },
];

function formatRegDate(v) {
  if (v == null || v === '') return '—';
  const ts = Number(v);
  const d = !isNaN(ts) && ts > 1000000000 ? new Date(ts * 1000) : new Date(v);
  if (isNaN(d)) return String(v);
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  let   h    = d.getHours();
  const i    = String(d.getMinutes()).padStart(2, '0');
  const s    = String(d.getSeconds()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${dd}/${mm}/${yyyy} ${h}:${i}:${s} ${ampm}`;
}

function formatCustomerValue(key, v) {
  if (v == null || v === '') return '—';
  if (key === 'regdate') return formatRegDate(v);
  return String(v);
}

function SectionCard({ icon, title, children, className = 'mb-3' }) {
  return (
    <CCard className={className}>
      <CCardHeader>
        <CIcon icon={icon} className="me-2 text-success" />{title}
      </CCardHeader>
      <CCardBody>{children}</CCardBody>
    </CCard>
  );
}

function FieldLabel({ icon, children }) {
  return (
    <CFormLabel className="d-flex align-items-center gap-2 mb-1">
      <CIcon icon={icon} size="sm" className="text-success" />{children}
    </CFormLabel>
  );
}

export default function WarrantyFormPage() {
  const { jobcode } = useParams();
  const isEdit = !!jobcode;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [history, setHistory] = useState([]);
  const [warrantyMeta, setWarrantyMeta] = useState(null); // { time, sms } from the warranty row
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [smsModal, setSmsModal] = useState({ visible: false, hid: null });
  const [waModal,  setWaModal]  = useState({ visible: false, hid: null });
  const [serialQrOpen, setSerialQrOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerInfoOpen, setCustomerInfoOpen] = useState(false);

  const applySerialFromQr = useCallback((text) => {
    setForm((f) => ({ ...f, no_siri: String(text || '').slice(0, 50) }));
  }, []);

  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    api.get('/warranty/suggested-jobcode')
      .then((r) => {
        if (cancelled) return;
        setForm((f) => {
          if (String(f.jobcode || '').trim()) return f;
          return { ...f, jobcode: r.data.jobcode || '' };
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/warranty/${jobcode}`)
      .then(r => {
        const { warranty: w, history: hist, customer: cust } = r.data;
        setForm({
          custid: w.custid || '',
          jobcode: w.jobcode || '',
          perhubungan: w.perhubungan || [],
          keutamaan: w.keutamaan || 'Biasa', catatan: w.catatan || '',
          jenis: w.jenis || '', kos: w.kos || '', alat: w.alat || [],
          peralatan_lain: w.peralatan_lain || '', status: w.status || 'Baru',
          no_siri: w.no_siri || '', tempat_claim: w.tempat_claim || '',
        });
        setHistory(hist || []);
        setWarrantyMeta({ time: w.time, sms: w.sms, admin_id: w.admin_id });
        setCustomer(cust || null);
      })
      .catch(() => setError('Failed to load warranty claim.'))
      .finally(() => setLoading(false));
  }, [jobcode, isEdit]);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function toggleArray(field, value) {
    setForm(f => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  }

  async function searchCustomer(e) {
    e.preventDefault();
    if (!customerSearch.trim()) return;
    try {
      const { data } = await api.get('/customers', { params: { search: customerSearch } });
      setCustomerResults(data.customers);
    } catch {
      setError('Customer search failed.');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      if (isEdit) {
        const { jobcode: _jc, ...payload } = form;
        await api.put(`/warranty/${jobcode}`, payload);
        setSuccess('Warranty claim updated.');
      } else {
        const payload = { ...form, jobcode: String(form.jobcode || '').trim() };
        const { data } = await api.post('/warranty', payload);
        setSuccess(`Claim created: ${data.warranty.jobcode}`);
        let nextCode = '';
        try {
          const s = await api.get('/warranty/suggested-jobcode');
          nextCode = s.data.jobcode || '';
        } catch { /* ignore */ }
        setForm({ ...EMPTY, jobcode: nextCode });
        setCustomerResults([]);
      }
    } catch (err) {
      const msgs = err.response?.data?.errors?.map(e => e.msg).join(', ');
      setError(msgs || err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-5"><CSpinner color="success" /></div>;

  return (
    <>
      <SerialQrScanModal
        visible={serialQrOpen}
        onClose={() => setSerialQrOpen(false)}
        onDecoded={applySerialFromQr}
      />

      <SmsModal
        jobcode={jobcode} hid={smsModal.hid} type="warranty"
        visible={smsModal.visible}
        onClose={() => setSmsModal({ visible: false, hid: null })}
        onSent={() => {
          setSmsModal({ visible: false, hid: null });
          api.get(`/warranty/${jobcode}`).then(r => {
            setHistory(r.data.history || []);
            setWarrantyMeta(m => ({ ...m, sms: 1 }));
          });
        }}
      />
      <WhatsAppModal
        jobcode={jobcode} hid={waModal.hid} type="warranty"
        visible={waModal.visible}
        onClose={() => setWaModal({ visible: false, hid: null })}
        onSent={() => {
          api.get(`/warranty/${jobcode}`).then(r => {
            setHistory(r.data.history || []);
            setWarrantyMeta(m => ({ ...m, sms: 1 }));
          });
        }}
      />

      <CModal visible={customerInfoOpen} onClose={() => setCustomerInfoOpen(false)} alignment="center" size="lg">
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilInfo} className="me-2 text-success" />
            Customer info
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {!customer ? (
            <p className="text-muted mb-0">No customer record linked to this warranty claim.</p>
          ) : (
            <CRow className="g-3 small">
              {CUSTOMER_INFO_FIELDS.map(({ key, label }) => (
                <CCol key={key} xs={12} md={6}>
                  <div className="text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: '0.04em' }}>{label}</div>
                  <div className="fw-medium text-break" style={{ whiteSpace: key === 'catatan' ? 'pre-wrap' : undefined }}>
                    {formatCustomerValue(key, customer[key])}
                  </div>
                </CCol>
              ))}
            </CRow>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setCustomerInfoOpen(false)}>Close</CButton>
        </CModalFooter>
      </CModal>

      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">{isEdit ? `Edit Warranty: ${jobcode}` : 'New Warranty Claim'}</h5></CCol>
        <CCol xs="auto" className="d-flex gap-2">
          {isEdit && (
            <>
              <CButton color="success" variant="outline" size="sm" onClick={() => setSmsModal({ visible: true, hid: null })}>
                <CIcon icon={cilSend} className="me-1" /> Send SMS
              </CButton>
              <CButton
                size="sm"
                variant="outline"
                style={{ borderColor: '#25d366', color: '#25d366' }}
                onClick={() => setWaModal({ visible: true, hid: null })}
                title="Open WhatsApp with customer number and the same default text as SMS (same as status history)"
              >
                <CIcon icon={cilSend} className="me-1" /> WhatsApp
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                size="sm"
                onClick={() => setCustomerInfoOpen(true)}
                title="View full customer record"
              >
                <CIcon icon={cilInfo} className="me-1" />
                Customer info
              </CButton>
            </>
          )}
          <CButton color="secondary" variant="outline" size="sm" onClick={() => navigate('/warranty')}>
            <CIcon icon={cilArrowLeft} className="me-1" /> Back
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {success && <CAlert color="success">{success}</CAlert>}

      <CForm onSubmit={handleSubmit}>
        <SectionCard icon={cilUser} title="Customer">
          {!isEdit ? (
            <>
              <FieldLabel icon={cilSearch}>Search Customer</FieldLabel>
              <CRow className="mb-2">
                <CCol md={8}>
                  <CFormInput
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    placeholder="Name or phone…"
                    onKeyDown={e => e.key === 'Enter' && searchCustomer(e)}
                  />
                </CCol>
                <CCol md={4}>
                  <CButton color="success" variant="outline" onClick={searchCustomer}>
                    <CIcon icon={cilSearch} className="me-1" /> Search
                  </CButton>
                </CCol>
              </CRow>
              {customerResults.length > 0 && (
                <div className="border rounded mt-1" style={{ maxHeight: 200, overflowY: 'auto', fontSize: 14 }}>
                  {customerResults.map(c => (
                    <div
                      key={c.uid}
                      className={`px-3 py-2 border-bottom ${form.custid === c.uid ? 'bg-success text-white' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setForm(f => ({ ...f, custid: c.uid }))}
                    >
                      <strong>{c.nama}</strong> — {c.telhp}
                      {form.custid === c.uid && <span className="ms-2">✓</span>}
                    </div>
                  ))}
                </div>
              )}
              {form.custid && <div className="mt-2 text-success small">Selected: {form.custid}</div>}
            </>
          ) : (
            <>
              <FieldLabel icon={cilUser}>Customer ID</FieldLabel>
              <CFormInput value={form.custid} disabled />
            </>
          )}
        </SectionCard>

        <SectionCard icon={cilShieldAlt} title="Warranty Info">
          <CRow className="mb-3">
            <CCol xs={12}>
              <FieldLabel icon={cilShieldAlt}>Receipt Number <font color="red">*</font></FieldLabel>
              {isEdit ? (
                <CFormInput value={form.jobcode || jobcode} disabled />
              ) : (
                <CFormInput
                  name="jobcode"
                  value={form.jobcode}
                  onChange={handleChange}
                  maxLength={20}
                  autoComplete="off"
                  placeholder="W + YYMMDD#### — auto-filled; edit if needed"
                />
              )}
              {!isEdit && (
                <div className="form-text small">
                  * Auto generated, editable.
                </div>
              )}
            </CCol>
          </CRow>
          <CRow className="mb-3">
            <CCol md={6}>
              <FieldLabel icon={cilShieldAlt}>Serial Number (No. Siri)</FieldLabel>
              <div className="d-flex flex-wrap gap-2 align-items-stretch">
                <CFormInput
                  name="no_siri"
                  value={form.no_siri}
                  onChange={handleChange}
                  maxLength={50}
                  placeholder="e.g. SN1234567890"
                  className="flex-grow-1"
                  style={{ minWidth: 160 }}
                />
                {!isEdit && (
                  <CButton
                    type="button"
                    color="secondary"
                    variant="outline"
                    className="text-nowrap"
                    onClick={() => setSerialQrOpen(true)}
                    title="Scan QR or barcode from device label"
                  >
                    <CIcon icon={cilCamera} className="me-1" />
                    Scan label
                  </CButton>
                )}
              </div>
              {!isEdit && (
                <div className="form-text small">Use camera to read a QR or barcode and fill this field (new claim only).</div>
              )}
            </CCol>
            <CCol md={6}>
              <FieldLabel icon={cilLocationPin}>Claim Location (Tempat Claim)</FieldLabel>
              <CFormInput name="tempat_claim" value={form.tempat_claim} onChange={handleChange} maxLength={100} placeholder="e.g. Petaling Jaya" />
            </CCol>
          </CRow>
          <CRow>
            <CCol md={6}>
              <FieldLabel icon={cilTask}>Priority</FieldLabel>
              <select name="keutamaan" value={form.keutamaan} onChange={handleChange} className="form-select">
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </CCol>
            <CCol md={6}>
              <FieldLabel icon={cilTask}>Status</FieldLabel>
              <select name="status" value={form.status} onChange={handleChange} className="form-select">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </CCol>
          </CRow>
        </SectionCard>

        <SectionCard icon={cilLaptop} title="Equipment">
          <CFormLabel className="mb-2">Devices</CFormLabel>
          <div className="d-flex gap-3 flex-wrap mb-3">
            {EQUIPMENT_OPTIONS.map(opt => (
              <CFormCheck key={opt} label={opt} checked={form.alat.includes(opt)} onChange={() => toggleArray('alat', opt)} />
            ))}
          </div>
          <CRow className="mb-3">
            <CCol md={8}>
              <FieldLabel icon={cilLaptop}>Equipment Type / Description</FieldLabel>
              <CFormInput name="jenis" value={form.jenis} onChange={handleChange} maxLength={200} placeholder="e.g. Laptop Acer Aspire" />
            </CCol>
            <CCol md={4}>
              <FieldLabel icon={cilTask}>Cost (RM)</FieldLabel>
              <CFormInput name="kos" value={form.kos} onChange={handleChange} maxLength={10} placeholder="Optional" />
            </CCol>
          </CRow>
          <FieldLabel icon={cilLaptop}>Other Equipment</FieldLabel>
          <CFormInput name="peralatan_lain" value={form.peralatan_lain} onChange={handleChange} maxLength={100} placeholder="Describe other items…" />
        </SectionCard>

        <SectionCard icon={cilPhone} title="Contact Methods">
          <div className="d-flex gap-3 flex-wrap">
            {CONTACT_OPTIONS.map(opt => (
              <CFormCheck key={opt} label={opt} checked={form.perhubungan.includes(opt)} onChange={() => toggleArray('perhubungan', opt)} />
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={cilNotes} title="Notes" className="mb-4">
          <CFormTextarea name="catatan" value={form.catatan} onChange={handleChange} rows={3} placeholder="Additional notes…" />
        </SectionCard>

        <div className="d-flex gap-2 mb-4">
          <CButton type="submit" color="success" disabled={saving || (!isEdit && !form.custid)}>
            <CIcon icon={cilSave} className="me-2" />
            {saving ? 'Saving…' : isEdit ? 'Update Claim' : 'Create Claim'}
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/warranty')}>Cancel</CButton>
        </div>
      </CForm>

      {isEdit && warrantyMeta && (
        <CCard>
          <CCardHeader>
            <CIcon icon={cilNotes} className="me-2 text-success" />Status History
          </CCardHeader>
          <CCardBody>
            {/* Creation row — matches old Laravel "Pendaftaran kerja baru" */}
            <div className="border-bottom py-2 d-flex align-items-center justify-content-between gap-2">
              <div style={{ fontSize: 14 }}>
                <span className="text-muted small me-2">
                  {new Date(parseInt(warrantyMeta.time) * 1000).toLocaleString()}
                </span>
                Pendaftaran kerja baru.
              </div>
              {!warrantyMeta.sms && (
                <div className="d-flex gap-1 flex-shrink-0">
                  <CButton size="sm" color="success" variant="outline" onClick={() => setSmsModal({ visible: true, hid: null })}>
                    <CIcon icon={cilSend} className="me-1" /> SMS
                  </CButton>
                  <CButton size="sm" variant="outline" style={{ borderColor: '#25d366', color: '#25d366' }} onClick={() => setWaModal({ visible: true, hid: null })}>
                    <CIcon icon={cilSend} className="me-1" /> WhatsApp
                  </CButton>
                </div>
              )}
            </div>
            {/* Subsequent status-change entries (skip any "created" entry inserted by the new backend) */}
            {history.filter(h => !/created with status/i.test(h.description || '')).map(h => (
              <div key={h.hid} className="border-bottom py-2 d-flex align-items-center justify-content-between gap-2">
                <div style={{ fontSize: 14 }}>
                  <span className="text-muted small me-2">
                    {new Date(parseInt(h.time) * 1000).toLocaleString()}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: h.description }} />
                </div>
                {h.needsms === 1 && h.smssent === 0 && (
                  <div className="d-flex gap-1 flex-shrink-0">
                    <CButton size="sm" color="success" variant="outline" onClick={() => setSmsModal({ visible: true, hid: h.hid })}>
                      <CIcon icon={cilSend} className="me-1" /> SMS
                    </CButton>
                    <CButton size="sm" variant="outline" style={{ borderColor: '#25d366', color: '#25d366' }} onClick={() => setWaModal({ visible: true, hid: h.hid })}>
                      <CIcon icon={cilSend} className="me-1" /> WhatsApp
                    </CButton>
                  </div>
                )}
              </div>
            ))}
          </CCardBody>
        </CCard>
      )}

      {isEdit && warrantyMeta?.admin_id && (
        <div className="text-muted small mt-2 text-end">
          Created by: <strong>{warrantyMeta.admin_id}</strong>
        </div>
      )}
    </>
  );
}
